import envService from "@/env.service";
import { useGetAnalysisReportByIdSuspense } from "@/spz-api/analysis-reports/analysis-reports";
import { useCallback, useEffect } from "react";
import { useParams } from "react-router";

/**
 * View model for handling report download functionality
 * - Validates user permissions (via Suspense + error boundary)
 * - Fetches report data (via Suspense)
 * - Automatically triggers download after 5 seconds
 */
export const selectReportDownloadViewModel = () => {
  // Extract route parameters and fetch user data
  const { reportId } = useParams();

  // Fetch report data with Suspense enabled
  // Errors will be thrown to the error boundary
  // Suspense will show loader while fetching
  const { data: report } = useGetAnalysisReportByIdSuspense(reportId as string);

  /**
   * Programmatically triggers file download using a temporary anchor element
   */
  const triggerDownload = useCallback(() => {
    const signedUrl = report?.data?.signedUrl;
    const filename = report?.data?.filename;

    if (!signedUrl || !filename) return;

    // Create temporary anchor element to trigger download
    const anchor = document.createElement("a");
    anchor.href = signedUrl;
    anchor.download = filename;

    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  }, [report?.data?.signedUrl, report?.data?.filename]);

  /**
   * Redirects user back to the main Supervizor application
   */
  const onNavigateToSupervizor = useCallback(() => {
    window.location.href = envService.env.auth0.callbackUrl;
  }, []);

  // Auto-trigger download after delay when report is ready
  useEffect(() => {
    const signedUrl = report?.data?.signedUrl;

    if (!signedUrl) return;

    // Add 5-second delay to ensure all resources are loaded
    const timeoutId = setTimeout(() => {
      triggerDownload();
    }, 5000);

    return () => clearTimeout(timeoutId);
  }, [triggerDownload, report?.data?.signedUrl]);

  return {
    onNavigateToSupervizor,
    triggerDownload,
  };
};
